import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@loot-os/ui/components/dropdown-menu";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Switch } from "@loot-os/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Building2, Ellipsis, Plus, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { TenantModuleIcons } from "../components/TenantModuleIcons";
import { CommercialTermsFields, TenantModulesFields } from "../components/TenantModulesFields";
import { TenantSettingsDialog } from "../components/TenantSettingsDialog";
import { formatDate } from "../lib/format";
import { slugify } from "../lib/slug";
import { defaultTenantModules, percentToBps } from "../lib/tenantModulesForm";
import { useTRPC } from "../lib/trpc";
import { usePlatformSwitchNotice } from "../lib/usePlatformSwitchNotice";

/** Mirrors the server's TENANT_SLUG_RE / MAX_TENANT_SLUG_LENGTH. */
const SLUG_RE = /^[a-z0-9](-?[a-z0-9])*$/;
const MAX_SLUG_LENGTH = 40;

function slugError(slug: string): string | null {
  if (!slug) return null;
  if (slug.length > MAX_SLUG_LENGTH) return `At most ${MAX_SLUG_LENGTH} characters.`;
  if (!SLUG_RE.test(slug)) {
    return "Lowercase letters, digits and single hyphens only; must start and end with a letter or digit.";
  }
  return null;
}

export function TenantsPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const tenants = useQuery(trpc.platform.tenants.list.queryOptions());
  // Fraud screening is on for every tenant and identity verification is on by default; the
  // latter's switch lives on each tenant's Settings page, so this list carries neither.

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  // Typing a name fills in the slug until staff edit the slug field themselves.
  const [slugTouched, setSlugTouched] = useState(false);
  // What the tenant starts with: every module on and 0% shares unless staff say otherwise.
  const [modules, setModules] = useState(defaultTenantModules);
  const [storefrontProfitShare, setStorefrontProfitShare] = useState("");
  const [shopifyPlatformShare, setShopifyPlatformShare] = useState("");
  // The row whose ⋯ menu opened the settings dialog, by id so a list refetch keeps it current.
  const [editingId, setEditingId] = useState<string | null>(null);
  // Archived tenants are frozen, so they're noise on the daily list; staff opt in to see them.
  const [showArchived, setShowArchived] = useState(false);

  const create = useMutation(
    trpc.platform.tenants.create.mutationOptions({
      onSuccess: ({ tenantId }) => {
        setShowCreate(false);
        setName("");
        setSlug("");
        setSlugTouched(false);
        setModules(defaultTenantModules());
        setStorefrontProfitShare("");
        setShopifyPlatformShare("");
        // Jump straight into the new tenant; the route's beforeLoad scopes everything.
        void navigate({ to: "/tenants/$tenantId", params: { tenantId } });
      },
    }),
  );

  const createSlugError = slugError(slug);
  const storefrontProfitShareBps = percentToBps(storefrontProfitShare);
  const shopifyPlatformShareBps = percentToBps(shopifyPlatformShare);
  const sharesError =
    storefrontProfitShareBps === null || shopifyPlatformShareBps === null
      ? "Shares are percents from 0 to 100, to two decimals."
      : null;
  const visible = tenants.data?.filter((tenant) => showArchived || !tenant.archivedAt) ?? [];
  const editingTenant = tenants.data?.find((tenant) => tenant.id === editingId);
  const hiddenCount = (tenants.data?.length ?? 0) - visible.length;

  usePlatformSwitchNotice();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tenants</h1>
          <p className="text-sm text-muted-foreground">
            Every tenant on the platform. Click a row to manage that tenant.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch id="show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
            <Label htmlFor="show-archived" className="text-sm text-muted-foreground">
              Show archived
            </Label>
          </div>
          <Button
            type="button"
            onClick={() => {
              create.reset();
              setShowCreate(true);
            }}
          >
            <Plus />
            Create tenant
          </Button>
        </div>
      </div>

      {tenants.error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {tenants.error.message}
        </div>
      )}

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Modules</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((tenant) => (
                <TableRow
                  key={tenant.id}
                  className="cursor-pointer"
                  onClick={() =>
                    void navigate({ to: "/tenants/$tenantId", params: { tenantId: tenant.id } })
                  }
                >
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2">
                      <Building2 className="size-4" />
                      {tenant.name}
                      {tenant.archivedAt && <Badge variant="outline">archived</Badge>}
                      {tenant.createdViaShopDomain && (
                        <Badge variant="secondary" title={tenant.createdViaShopDomain}>
                          Shopify install
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <TenantModuleIcons switches={tenant.moduleSwitches} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(tenant.createdAt)}
                  </TableCell>
                  {/* The menu's clicks bubble through its portal to the row; stop them here so
                      opening it never opens the tenant. */}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`More actions for ${tenant.name}`}
                          />
                        }
                      >
                        <Ellipsis />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-40">
                        <DropdownMenuItem onClick={() => setEditingId(tenant.id)}>
                          <SlidersHorizontal />
                          Edit settings
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {tenants.data && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    {hiddenCount > 0
                      ? `${hiddenCount} archived ${hiddenCount === 1 ? "tenant is" : "tenants are"} hidden.`
                      : "No tenants yet — create the first one."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editingTenant && (
        <TenantSettingsDialog
          key={editingTenant.id}
          tenant={editingTenant}
          onClose={() => setEditingId(null)}
        />
      )}

      <Dialog open={showCreate} onOpenChange={(open) => !open && setShowCreate(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create tenant</DialogTitle>
            <DialogDescription>
              A new tenant with its own boxes, users, API keys and storefront. The slug becomes its
              storefront subdomain and can't be changed later.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (
                createSlugError ||
                storefrontProfitShareBps === null ||
                shopifyPlatformShareBps === null
              ) {
                return;
              }
              create.mutate({
                name,
                slug,
                modules,
                commercialTerms: { storefrontProfitShareBps, shopifyPlatformShareBps },
              });
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="tenant-name">Name</Label>
              <Input
                id="tenant-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(slugify(e.target.value, MAX_SLUG_LENGTH));
                }}
                placeholder="Acme Collectibles"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="tenant-slug">Slug</Label>
              <Input
                id="tenant-slug"
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value.toLowerCase());
                }}
                placeholder="acme"
                maxLength={MAX_SLUG_LENGTH}
                required
              />
              {createSlugError && <p className="text-sm text-destructive">{createSlugError}</p>}
            </div>
            <fieldset className="grid gap-2">
              <legend className="mb-2 text-sm font-medium">Modules</legend>
              <p className="text-sm text-muted-foreground">
                What the tenant can use. A module that is off is gone from its dashboard; you can
                change these later on the tenant's Settings page.
              </p>
              <TenantModulesFields idPrefix="create-module" value={modules} onChange={setModules} />
            </fieldset>
            <fieldset className="grid gap-2">
              <legend className="mb-2 text-sm font-medium">Revenue shares</legend>
              <p className="text-sm text-muted-foreground">
                The storefront share is for reporting only. Loot Boxes for Shopify bills the Shopify
                fee as usage charges on the merchant's Shopify invoice.
              </p>
              <CommercialTermsFields
                idPrefix="create"
                storefrontProfitShare={storefrontProfitShare}
                shopifyPlatformShare={shopifyPlatformShare}
                onStorefrontProfitShareChange={setStorefrontProfitShare}
                onShopifyPlatformShareChange={setShopifyPlatformShare}
              />
              {sharesError && <p className="text-sm text-destructive">{sharesError}</p>}
            </fieldset>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  create.isPending ||
                  !name.trim() ||
                  !slug.trim() ||
                  createSlugError !== null ||
                  sharesError !== null
                }
              >
                {create.isPending ? "Creating…" : "Create tenant"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
